import { observer } from "mobx-react-lite";
import React, {
  CSSProperties,
  ChangeEventHandler,
  FocusEventHandler,
  HTMLInputTypeAttribute,
} from "react";
import * as XLSX from "xlsx";
import {
  IBrandData,
  IFileDetails,
  IInverterTypes,
  IProductCategory,
  IProductSheet,
  ProductForm,
} from "../../../../models/product";

interface InputTempProps {
  label?: string;
  name?: string;
  id?: string;
  inputType?: HTMLInputTypeAttribute | undefined;
  placeholder?: string;
  defaultValue?: any;
  value?: string;
  // children?: ReactNode;
  visibilityPadding?: boolean;
  marginRight?: boolean;
  marginLeft?: boolean;
  marginLeftSm?: boolean;
  marginRightSm?: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement> | undefined;
  mode?: "light" | "dark";
  width?: string;
  style?: CSSProperties;
  inputStyle?: string;
  labelStyle?: CSSProperties;
  disabled?: boolean;
  autoComplete?: string;
  excelFileError?: string;
  categoryList?: IProductCategory[];
  brandsList?: IBrandData[];
  inverterTypesList?: IInverterTypes[];
  inputField: ProductForm[];
  setInputField: React.Dispatch<React.SetStateAction<ProductForm[]>>;
  setExcelFileError?: any;
  fileDetailsList: IFileDetails[];
  setFileDetailsList: React.Dispatch<React.SetStateAction<IFileDetails[]>>;
  bulkRef?: React.RefObject<HTMLInputElement>;
}

const ProductBulkUploadInput: React.FC<InputTempProps> = ({
  name,
  id,
  label,
  inputType,
  placeholder,
  defaultValue,
  value,
  onChange,
  onBlur,
  // children,
  visibilityPadding,
  marginRight,
  marginLeft,
  marginLeftSm,
  marginRightSm,
  mode = "dark",
  width,
  style,
  disabled,
  autoComplete,
  inputStyle = {},
  labelStyle = {},
  excelFileError = "",
  categoryList,
  brandsList,
  inverterTypesList,
  inputField,
  setInputField,
  setExcelFileError,
  fileDetailsList,
  setFileDetailsList,
  bulkRef,
}) => {
  const clearForm = () => {
    setFileDetailsList([]);
    setInputField([]);
  };
  const onInputClick = (
    event: React.MouseEvent<HTMLInputElement, MouseEvent>
  ) => {
    const element = event.target as HTMLInputElement;
    element.value = "";
  };
  const imageHandler = (e: any) => {
    // console.log(inputField);
    // e.target.files[0]
    const UPLOAD_LIMIT = Number(process.env.REACT_APP_UPLOAD_LIMIT);
    if (isNaN(UPLOAD_LIMIT)) {
      setExcelFileError("Upload limit not configured. Set REACT_APP_UPLOAD_LIMIT in .env");
      setFileDetailsList([]);
      return;
    }
    if (e.target.files[0].size > UPLOAD_LIMIT) {
      setExcelFileError(
        "File is too large. Please upload a file less than the configured REACT_APP_UPLOAD_LIMIT"
      );
      setFileDetailsList([]);
    } else {
      try {
        const reader = new FileReader();

        reader.onload = () => {
          if (reader.readyState === 2) {
            const workbook = XLSX.read(reader.result, { type: "buffer" });
            const worksheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[worksheetName];
            const data = JSON.parse(
              JSON.stringify(XLSX.utils.sheet_to_json(worksheet))
            );
            // console.log(data);
            let file: IProductSheet[] = [];
            try {
              file = data;
              if (file.length > 0) {
                // console.log(file);
                let values: ProductForm[] = [];
                for (let i = 0; i < file.length; i++) {
                  const value = new ProductForm();
                  if (
                    file[i].Category &&
                    file[i].Category === "Inverters" &&
                    (!file[i]["BatteryType(Inverters)"] ||
                      !file[i]["BatteryTypeValue(Inverters)"] ||
                      !file[i]["MinimumInverterCapacity(Inverters)"])
                  ) {
                    const error = "Inverters";
                    throw error;
                  } else if (!file[i].Name) {
                    const error = "Name Cell";
                    throw error;
                  } else if (!file[i].Price || isNaN(file[i].Price)) {
                    const error = "Price Cell";
                    throw error;
                  } else if (!file[i].Note) {
                    const error = "Note Cell";
                    throw error;
                  } else if (
                    !file[i]["Weight(KG)"] ||
                    isNaN(file[i]["Weight(KG)"])
                  ) {
                    const error = "Weight Cell";
                    throw error;
                  } else if (!file[i].Quantity || isNaN(file[i].Quantity)) {
                    const error = "Quantity Cell";
                    throw error;
                  } else if (!file[i].Description) {
                    const error = "Description Cell";
                    throw error;
                  } else if (typeof file[i].CarryOutDelivery !== "boolean") {
                    const error = "CarryOutDelivery";
                    throw error;
                  } else if (
                    file[i].ImageUrl1 &&
                    !file[i].ImageUrl1.includes("https://")
                  ) {
                    const error = "ImageUrl1";
                    throw error;
                  } else if (
                    file[i].ImageUrl2 &&
                    !file[i].ImageUrl2.includes("https://")
                  ) {
                    const error = "ImageUrl2";
                    throw error;
                  } else if (
                    file[i].ImageUrl3 &&
                    !file[i].ImageUrl3.includes("https://")
                  ) {
                    const error = "ImageUrl3";
                    throw error;
                  } else if (
                    file[i].ImageUrl4 &&
                    !file[i].ImageUrl4.includes("https://")
                  ) {
                    const error = "ImageUrl4";
                    throw error;
                  } else if (
                    file[i].ImageUrl5 &&
                    !file[i].ImageUrl5.includes("https://")
                  ) {
                    const error = "ImageUrl5";
                    throw error;
                  } else {
                    let multipleImageList = [];
                    if (file[i].ImageUrl1) {
                      multipleImageList.push(file[i].ImageUrl1);
                    }
                    if (file[i].ImageUrl2) {
                      multipleImageList.push(file[i].ImageUrl2);
                    }
                    if (file[i].ImageUrl3) {
                      multipleImageList.push(file[i].ImageUrl3);
                    }
                    if (file[i].ImageUrl4) {
                      multipleImageList.push(file[i].ImageUrl4);
                    }
                    if (file[i].ImageUrl5) {
                      multipleImageList.push(file[i].ImageUrl5);
                    }
                    values.push({
                      ...value,
                      productName: file[i].Name,
                      brandId:
                        file[i].Brand &&
                        brandsList !== undefined &&
                        brandsList.length > 0
                          ? brandsList[
                              brandsList.findIndex(
                                (x) => x.brandName === file[i].Brand
                              )
                            ].brandId
                          : "",
                      productCurrentPrice: file[i].Price,
                      categoryId:
                        categoryList !== undefined && categoryList.length > 0
                          ? categoryList[
                              categoryList.findIndex(
                                (x) => x.category === file[i].Category
                              )
                            ].categoryId
                          : "",
                      minIvtCap:
                        file[i].Category && file[i].Category === "Inverters"
                          ? String(
                              file[i]["MinimumInverterCapacity(Inverters)"]
                            )
                          : "",
                      batType:
                        file[i].Category &&
                        file[i].Category === "Inverters" &&
                        inverterTypesList !== undefined &&
                        inverterTypesList.length > 0
                          ? inverterTypesList[
                              inverterTypesList.findIndex(
                                (x) =>
                                  x.batteryInvtName ===
                                  file[i]["BatteryType(Inverters)"]
                              )
                            ].batteryInvtId
                          : "",
                      batTypeValue:
                        file[i].Category && file[i].Category === "Inverters"
                          ? String(file[i]["BatteryTypeValue(Inverters)"])
                          : "",
                      buyerNote: file[i].Note,
                      weight: file[i]["Weight(KG)"],
                      isDeliveryCarryOut: file[i].CarryOutDelivery
                        ? true
                        : false,
                      productQuantity: file[i].Quantity,
                      longDescription: file[i].Description,
                      productPrimaryImageUrl: file[i].ImageUrl1
                        ? file[i].ImageUrl1
                        : file[i].ImageUrl2
                        ? file[i].ImageUrl2
                        : file[i].ImageUrl3
                        ? file[i].ImageUrl3
                        : file[i].ImageUrl4
                        ? file[i].ImageUrl4
                        : file[i].ImageUrl5
                        ? file[i].ImageUrl5
                        : "",
                      multipleProductImageUrl: multipleImageList,
                    });
                  }
                }
                // console.log(values);
                setInputField([...inputField, ...values]);
                setFileDetailsList([e.target.files[0]]);
                setExcelFileError(null);
              } else {
                // console.log(file);
                setExcelFileError("Invalid File Input");
                clearForm();
              }
            } catch (error) {
              // console.log(error);
              const errorString = String(error);
              if (
                errorString.includes(
                  "Cannot read properties of undefined (reading 'brandId')"
                )
              ) {
                setExcelFileError(
                  "Please Check if a Brand Cell in the uploaded file is a valid Brand from the Helper File"
                );
              } else if (
                errorString.includes(
                  "Cannot read properties of undefined (reading 'categoryId')"
                )
              ) {
                setExcelFileError(
                  "Please Check if a Category Cell in the uploaded file is a valid Category from the Option Helper File "
                );
              } else if (
                errorString.includes(
                  "Cannot read properties of undefined (reading 'batteryInvtId')"
                )
              ) {
                setExcelFileError(
                  "Please Check if a BatteryType(Inverters) Cell in the uploaded file is a valid BatteryType(Inverters) from the Option Helper File"
                );
              } else if (errorString.includes("Cell")) {
                setExcelFileError(
                  `Please Check if a ${errorString} value in the uploaded file is valid`
                );
              } else if (errorString.includes("ImageUrl")) {
                setExcelFileError(
                  `Please Check if a ${errorString} Cell value in the uploaded file is valid and is an https url`
                );
              } else if (errorString.includes("Inverters")) {
                setExcelFileError(
                  `BatteryType(Inverters), BatteryTypeValue(Inverters) & MinimumInverterCapacity(Inverters) cells are required if the category is 'Inverters'`
                );
              } else if (errorString.includes("CarryOutDelivery")) {
                setExcelFileError(
                  `CarryOutDelivery cell can only either be 'TRUE' or 'FALSE'`
                );
              } else setExcelFileError("Invalid File Format");
              clearForm();
            }
          }
        };
        reader.readAsArrayBuffer(e.target.files[0]);
      } catch {
        // setInputField(null);
        setExcelFileError("No File Selected");
        clearForm();
      }
    }
  };
  return (
    <input
      // hidden
      accept=".csv"
      name={name}
      type="file"
      // ref={bulkRef}
      onChange={(e: any) => imageHandler(e)}
      onClick={onInputClick}
    />
  );
};

export default observer(ProductBulkUploadInput);
